import React, { useState, useEffect } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import api from '../services/api';
import { toast } from 'sonner';
import LiveAuctionScreen from './LiveAuctionScreen';
import {
  ChevronLeft, FileText, ClipboardList, File, MessageSquare, Send,
  CheckCircle, Lock, Download, Eye, Gavel, Clock, Upload, Paperclip
} from 'lucide-react';

const Q_INPUT = {
  yes_no: 'radio', single_select: 'radio', multi_select: 'checkbox',
  short_text: 'text', long_text: 'textarea', number: 'number',
  free_text: 'textarea', date: 'date', file_upload: 'file',
  numeric: 'number', rating: 'rating', multiple_choice: 'checkbox',
};

export default function SupplierEventDetail({ event, token, onBack, PORTAL_API }) {
  const authH = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState('overview');
  const [answers, setAnswers] = useState({});
  const [pricingBids, setPricingBids] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [newQuestionSubject, setNewQuestionSubject] = useState('');

  const isAuction = event.type === 'REVERSE_AUCTION';
  const isRFQ = event.type === 'RFQ';
  const isRFP = event.type === 'RFP';
  const isRFI = event.type === 'RFI';
  const alreadyResponded = !!event.my_response;
  const isOpen = event.status === 'OPEN';

  // Initialize pricing bids from line items
  useEffect(() => {
    if (isRFQ && event.line_items?.length) {
      setPricingBids(event.line_items.map(item => ({
        item_id: item.item_id || item.id, description: item.description,
        quantity: item.quantity || 1, uom: item.uom || 'EA',
        unit_price: '', lead_time: '', payment_terms: '', comments: '',
      })));
    }
  }, [event.line_items, isRFQ]);

  const [acceptLoading, setAcceptLoading] = useState(false);

  // Load Q&A on mount
  useEffect(() => {
    api.get(`${PORTAL_API}/event/${event.event_ref}/questions`, authH)
      .then(r => setQuestions(r.data.data || []))
      .catch(() => {});
  }, [event.event_ref]); // eslint-disable-line

  const handleAcceptInvitation = async () => {
    setAcceptLoading(true);
    try {
      await api.post(`${PORTAL_API}/event/${event.event_ref}/accept-invitation`, {}, authH);
      toast.success('Invitation accepted');
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setAcceptLoading(false);
  };

  const handleDeclineInvitation = async () => {
    const reason = window.prompt('Reason for declining (optional):') || '';
    setAcceptLoading(true);
    try {
      await api.post(`${PORTAL_API}/event/${event.event_ref}/decline-invitation`, { reason }, authH);
      toast.success('Invitation declined');
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setAcceptLoading(false);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FileText },
    { id: 'requirements', label: 'Requirements', icon: ClipboardList },
    { id: 'documents', label: 'Documents', icon: File },
    { id: 'questions', label: 'Questions', icon: MessageSquare },
    ...(!isAuction ? [{ id: 'response', label: 'Submit Response', icon: Send }] : []),
    ...(isAuction ? [{ id: 'auction', label: 'Live Auction', icon: Gavel }] : []),
  ];

  // Submit RFI/RFP questionnaire response
  const handleSubmitQuestionnaire = async () => {
    setSubmitting(true);
    try {
      const r = await api.post(`${PORTAL_API}/event/${event.event_ref}/respond`, { answers }, authH);
      toast.success(r.data.message);
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    setSubmitting(false);
  };

  // Submit RFQ pricing response
  const handleSubmitPricing = async () => {
    const validBids = pricingBids.filter(b => parseFloat(b.unit_price) > 0);
    if (validBids.length === 0) { toast.error('Enter at least one price'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`${PORTAL_API}/event/${event.event_ref}/respond`, {
        answers: {}, pricing_response: validBids,
      }, authH);
      toast.success(r.data.message);
      onBack();
    } catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
    setSubmitting(false);
  };

  // If showing live auction, render the full-screen auction component
  if (tab === 'auction' && isAuction) {
    return <LiveAuctionScreen event={event} token={token} onBack={() => setTab('overview')} />;
  }

  return (
    <div data-testid="supplier-event-detail-v2">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline mb-3">
        <ChevronLeft className="w-4 h-4" /> Back to Events
      </button>

      {/* Event Header */}
      <div className="border rounded-xl p-5 bg-white mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Badge className={`text-xs ${isAuction ? 'bg-rose-100 text-rose-700' : 'bg-indigo-100 text-indigo-700'}`}>
            {isAuction ? 'Auction' : event.type}
          </Badge>
          <span className="font-mono text-xs text-slate-500">{event.event_ref}</span>
          <Badge className="text-[9px] bg-slate-100 text-slate-600">{event.status}</Badge>
          {event.sealed_bidding?.enabled && <Badge className="text-[9px] bg-amber-100 text-amber-700"><Lock className="w-3 h-3 mr-0.5 inline" /> Sealed</Badge>}
          {alreadyResponded && <Badge className="text-[9px] bg-emerald-100 text-emerald-700"><CheckCircle className="w-3 h-3 mr-0.5 inline" /> Responded</Badge>}
        </div>
        <h2 className="text-lg font-bold">{event.title}</h2>
        {event.closing_date && <p className="text-xs text-red-600 font-semibold mt-1"><Clock className="w-3 h-3 inline mr-1" />Deadline: {new Date(event.closing_date).toLocaleString()}</p>}
      </div>

      {/* Accept/Decline invitation banner */}
      {isOpen && !alreadyResponded && event.relationship === 'INVITED_NOT_RESPONDED' && (
        <div className="border-2 border-indigo-200 rounded-xl p-4 bg-indigo-50 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-indigo-900">You've been invited to participate</p>
              <p className="text-xs text-indigo-700 mt-0.5">Accept the invitation to view full details and submit your response.</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleDeclineInvitation} variant="outline" disabled={acceptLoading}
                className="border-red-300 text-red-600 hover:bg-red-50" data-testid="decline-invitation-btn">
                Decline
              </Button>
              <Button size="sm" onClick={handleAcceptInvitation} disabled={acceptLoading}
                className="bg-indigo-600 hover:bg-indigo-700" data-testid="accept-invitation-btn">
                <CheckCircle className="w-3.5 h-3.5 mr-1" /> {acceptLoading ? 'Processing...' : 'Accept Invitation'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 border-b mb-4">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {tab === 'overview' && (
        <div className="space-y-4">
          {event.description && <div className="border rounded-xl p-4 bg-white"><h3 className="text-sm font-semibold mb-2">Description</h3><p className="text-sm text-slate-700 whitespace-pre-wrap">{event.description}</p></div>}
          <div className="grid grid-cols-2 gap-4">
            <div className="border rounded-xl p-4 bg-white"><h3 className="text-sm font-semibold mb-2">Key Dates</h3>
              <div className="space-y-1 text-sm">
                {event.publish_date && <p><span className="text-slate-500 w-32 inline-block">Published:</span>{new Date(event.publish_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' })}</p>}
                {event.question_deadline && <p><span className="text-slate-500 w-32 inline-block">Questions close:</span>{new Date(event.question_deadline).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' })}</p>}
                <p><span className="text-slate-500 w-32 inline-block">Response due:</span><strong>{event.closing_date ? new Date(event.closing_date).toLocaleString() : '—'}</strong></p>
              </div>
            </div>
            {event.supplier_instructions && <div className="border rounded-xl p-4 bg-white"><h3 className="text-sm font-semibold mb-2">Instructions</h3><p className="text-sm text-slate-700 whitespace-pre-wrap">{event.supplier_instructions}</p></div>}
          </div>
        </div>
      )}

      {/* ── REQUIREMENTS TAB ── */}
      {tab === 'requirements' && (
        <div className="space-y-4">
          {event.requirement_summary && <div className="border rounded-xl p-4 bg-white"><p className="text-sm">{event.requirement_summary}</p></div>}
          {(event.line_items || event.auction_items || []).length > 0 && (
            <div className="border rounded-xl bg-white overflow-hidden">
              <h3 className="text-sm font-semibold p-4 border-b">Line Items</h3>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-50 border-b"><th className="text-left px-4 py-2">#</th><th className="text-left px-4 py-2">Description</th><th className="text-right px-4 py-2">Qty</th><th className="text-left px-4 py-2">UOM</th></tr></thead>
                <tbody>{(event.line_items || event.auction_items || []).map((item, i) => (
                  <tr key={i} className="border-b"><td className="px-4 py-2 text-xs">{i+1}</td><td className="px-4 py-2">{item.description}</td><td className="px-4 py-2 text-right">{item.quantity}</td><td className="px-4 py-2">{item.uom}</td></tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {(event.lots || event.auction_lots || []).length > 0 && (
            <div className="border rounded-xl bg-white p-4">
              <h3 className="text-sm font-semibold mb-3">Lots</h3>
              {(event.lots || event.auction_lots || []).map((lot, i) => (
                <div key={i} className="border rounded-lg p-3 mb-2"><strong>{lot.name}</strong>{lot.description && <span className="text-slate-500 ml-2">— {lot.description}</span>}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── DOCUMENTS TAB ── */}
      {tab === 'documents' && (
        <div className="border rounded-xl bg-white p-4">
          <h3 className="text-sm font-semibold mb-3">Supporting Documents</h3>
          {(event.documents || []).length === 0 ? <p className="text-xs text-slate-400">No documents available.</p> : (
            <div className="space-y-2">
              {event.documents.map((d, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                  <div className="flex items-center gap-2"><Paperclip className="w-4 h-4 text-slate-400" /><span className="text-sm">{d.file_name}</span></div>
                  <button onClick={async () => {
                    try { const r = await api.get(`${PORTAL_API}/event/${event.event_ref}/documents/${d.file_name}`, { ...authH, responseType: 'blob' });
                      const url = URL.createObjectURL(new Blob([r.data])); const a = document.createElement('a'); a.href = url; a.download = d.file_name; a.click();
                    } catch { toast.error('Download failed'); }
                  }} className="flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 px-3 py-1.5 rounded bg-indigo-50 border border-indigo-200">
                    <Download className="w-3.5 h-3.5" /> Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── QUESTIONS TAB ── */}
      {tab === 'questions' && (
        <div className="space-y-4">
          {/* Ask a Question */}
          {isOpen && (
            <div className="border rounded-xl bg-white p-4">
              <h3 className="text-sm font-semibold mb-2">Ask a Question</h3>
              <p className="text-xs text-slate-500 mb-3">Your question will be sent to the sourcing team. Once answered, the Q&A will be published and visible to all participating suppliers.</p>
              <div className="space-y-2">
                <Input value={newQuestionSubject || ''} onChange={e => setNewQuestionSubject(e.target.value)} placeholder="Subject (optional)" className="text-sm" />
                <div className="flex gap-2">
                  <Input value={newQuestion} onChange={e => setNewQuestion(e.target.value)} placeholder="Type your question..." className="flex-1" />
                  <Button size="sm" disabled={!newQuestion.trim()} onClick={async () => {
                    try {
                      await api.post(`${PORTAL_API}/event/${event.event_ref}/question`, { subject: newQuestionSubject, message: newQuestion }, authH);
                      toast.success('Question submitted — the sourcing team will respond');
                      setNewQuestion(''); setNewQuestionSubject('');
                      // Reload Q&A
                      const r = await api.get(`${PORTAL_API}/event/${event.event_ref}/questions`, authH);
                      setQuestions(r.data.data || []);
                    } catch (e) { toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to submit question'); }
                  }}>
                    <Send className="w-3.5 h-3.5 mr-1" /> Ask
                  </Button>
                </div>
              </div>
            </div>
          )}
          {/* Published Q&A */}
          <div className="border rounded-xl bg-white p-4">
            <h3 className="text-sm font-semibold mb-3">Q&A ({questions.length})</h3>
            {questions.length === 0 ? (
              <p className="text-xs text-slate-400 text-center py-4">No questions or answers published yet.</p>
            ) : (
              <div className="space-y-3">
                {questions.map((q, i) => (
                  <div key={q.id || i} className="border rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded mt-0.5">Q</span>
                      <div className="flex-1">
                        {q.subject && <p className="text-xs font-semibold text-slate-700">{q.subject}</p>}
                        <p className="text-sm text-slate-800">{q.message}</p>
                        <p className="text-[10px] text-slate-400 mt-1">Asked by {q.from_supplier || 'Supplier'} — {q.posted_at ? new Date(q.posted_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                      </div>
                    </div>
                    {q.answered && q.answer && (
                      <div className="flex items-start gap-2 mt-2 pl-6 border-t pt-2">
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded mt-0.5">A</span>
                        <div className="flex-1">
                          <p className="text-sm text-slate-800">{q.answer}</p>
                          <p className="text-[10px] text-slate-400 mt-1">Answered by {q.answered_by || 'Buyer'} — {q.answered_at ? new Date(q.answered_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                        </div>
                      </div>
                    )}
                    {!q.answered && q.from_supplier_id === 'self' && (
                      <p className="text-[10px] text-amber-600 mt-1 pl-6">Awaiting answer from the sourcing team</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── RESPONSE TAB — RFI/RFP Questionnaire ── */}
      {tab === 'response' && (isRFI || isRFP) && (
        <div className="border rounded-xl bg-white p-6">
          {alreadyResponded ? (
            <div className="text-center py-8">
              <CheckCircle className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              <h3 className="font-semibold text-lg">Response Submitted</h3>
              <p className="text-sm text-slate-500 mt-1">Submitted on {event.my_response.submitted_at ? new Date(event.my_response.submitted_at).toLocaleString() : ''}</p>
              {event.my_response.percentage_score != null && <p className="text-sm text-slate-500">Score: {event.my_response.percentage_score}%</p>}
              {/* Show submitted answers */}
              {event.my_response.answers?.length > 0 && (
                <div className="text-left mt-6 space-y-3">
                  {event.my_response.answers.map((a, i) => (
                    <div key={i} className="border rounded p-3">
                      <p className="text-xs text-slate-500">{a.section}</p>
                      <p className="text-sm font-medium">{a.question_text}</p>
                      <p className="text-sm text-indigo-700 mt-1">{a.value || '(no answer)'}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              <h3 className="text-sm font-semibold mb-4">{isRFI ? 'Questionnaire' : 'Proposal Response'}</h3>
              {(event.sections || []).map((section, sIdx) => (
                <div key={sIdx} className="mb-6">
                  <h4 className="text-sm font-semibold text-slate-700 border-b pb-1 mb-3">{section.section_name}</h4>
                  <div className="space-y-4">
                    {(section.questions || []).map((q, qIdx) => {
                      const inputType = Q_INPUT[q.type] || 'text';
                      return (
                        <div key={q.question_id} className="space-y-1">
                          <p className="text-sm font-medium">{sIdx+1}.{qIdx+1} {q.text} {q.required && <span className="text-red-500">*</span>}</p>
                          {inputType === 'textarea' && <Textarea value={answers[q.question_id]?.value || ''} onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { value: e.target.value } }))} rows={3} />}
                          {inputType === 'text' && <Input value={answers[q.question_id]?.value || ''} onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { value: e.target.value } }))} />}
                          {inputType === 'number' && <Input type="number" value={answers[q.question_id]?.value || ''} onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { value: e.target.value } }))} className="w-48" />}
                          {inputType === 'date' && <Input type="date" value={answers[q.question_id]?.value || ''} onChange={e => setAnswers(a => ({ ...a, [q.question_id]: { value: e.target.value } }))} className="w-48" />}
                          {inputType === 'radio' && (q.type === 'yes_no' ? ['Yes', 'No'] : (q.options || [])).map(opt => (
                            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="radio" name={`q_${q.question_id}`} checked={answers[q.question_id]?.value === opt}
                                onChange={() => setAnswers(a => ({ ...a, [q.question_id]: { value: opt } }))} /> {opt}
                            </label>
                          ))}
                          {inputType === 'checkbox' && (q.options || []).map(opt => (
                            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                              <input type="checkbox" checked={(answers[q.question_id]?.value || '').split(',').includes(opt)}
                                onChange={e => { const curr = (answers[q.question_id]?.value || '').split(',').filter(Boolean);
                                  setAnswers(a => ({ ...a, [q.question_id]: { value: (e.target.checked ? [...curr, opt] : curr.filter(c => c !== opt)).join(',') } })); }} /> {opt}
                            </label>
                          ))}
                          {inputType === 'rating' && <div className="flex gap-1">{[1,2,3,4,5,6,7,8,9,10].map(n => (
                            <button key={n} onClick={() => setAnswers(a => ({ ...a, [q.question_id]: { value: String(n) } }))}
                              className={`w-8 h-8 rounded border text-xs font-medium ${String(answers[q.question_id]?.value) === String(n) ? 'bg-indigo-600 text-white border-indigo-600' : 'hover:bg-slate-100'}`}>{n}</button>
                          ))}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {isRFP && (
                <div className="border-t pt-4 mt-4 space-y-3">
                  <h4 className="text-sm font-semibold">Proposal Uploads</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="border-2 border-dashed rounded-lg p-4 text-center text-xs text-slate-400 cursor-pointer hover:border-indigo-300">
                      <Upload className="w-5 h-5 mx-auto mb-1" /> Technical Proposal (PDF)
                    </div>
                    <div className="border-2 border-dashed rounded-lg p-4 text-center text-xs text-slate-400 cursor-pointer hover:border-indigo-300">
                      <Upload className="w-5 h-5 mx-auto mb-1" /> Commercial Proposal (PDF)
                    </div>
                  </div>
                </div>
              )}
              <div className="border-t pt-4 mt-4">
                <Button onClick={handleSubmitQuestionnaire} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
                  {submitting ? 'Submitting...' : 'Submit Response'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── RESPONSE TAB — RFQ Pricing ── */}
      {tab === 'response' && isRFQ && (
        <div className="border rounded-xl bg-white p-6">
          {alreadyResponded ? (
            <div className="text-center py-8">
              <CheckCircle className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              <h3 className="font-semibold text-lg">Pricing Response Submitted</h3>
              <p className="text-sm text-slate-500 mt-1">Submitted on {event.my_response?.submitted_at ? new Date(event.my_response.submitted_at).toLocaleString() : ''}</p>
            </div>
          ) : (
            <>
              <h3 className="text-sm font-semibold mb-4">Pricing Response</h3>
              <p className="text-xs text-slate-500 mb-3">Enter your unit price for each line item. Total is calculated automatically.</p>
              <div className="border rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b">
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Description</th>
                    <th className="text-right px-3 py-2">Qty</th>
                    <th className="text-left px-3 py-2">UOM</th>
                    <th className="text-right px-3 py-2">Your Unit Price</th>
                    <th className="text-right px-3 py-2">Total</th>
                    <th className="text-left px-3 py-2">Lead Time</th>
                    <th className="text-left px-3 py-2">Comments</th>
                  </tr></thead>
                  <tbody>
                    {pricingBids.map((bid, idx) => {
                      const total = (parseFloat(bid.unit_price) || 0) * (bid.quantity || 1);
                      return (
                        <tr key={idx} className="border-b">
                          <td className="px-3 py-2 text-xs">{idx + 1}</td>
                          <td className="px-3 py-2">{bid.description}</td>
                          <td className="px-3 py-2 text-right">{bid.quantity}</td>
                          <td className="px-3 py-2">{bid.uom}</td>
                          <td className="px-3 py-1 text-right">
                            <Input type="number" value={bid.unit_price} onChange={e => {
                              const u = [...pricingBids]; u[idx] = { ...u[idx], unit_price: e.target.value }; setPricingBids(u);
                            }} className="h-7 text-xs w-24 text-right" placeholder="0.00" />
                          </td>
                          <td className="px-3 py-2 text-right font-bold">{total > 0 ? `$${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}</td>
                          <td className="px-3 py-1"><Input value={bid.lead_time || ''} onChange={e => { const u = [...pricingBids]; u[idx] = { ...u[idx], lead_time: e.target.value }; setPricingBids(u); }} className="h-7 text-xs w-20" placeholder="days" /></td>
                          <td className="px-3 py-1"><Input value={bid.comments || ''} onChange={e => { const u = [...pricingBids]; u[idx] = { ...u[idx], comments: e.target.value }; setPricingBids(u); }} className="h-7 text-xs w-32" placeholder="Notes" /></td>
                        </tr>
                      );
                    })}
                    <tr className="bg-slate-50 font-bold">
                      <td colSpan={5} className="px-3 py-2 text-right">Grand Total:</td>
                      <td className="px-3 py-2 text-right text-indigo-700">
                        ${pricingBids.reduce((s, b) => s + (parseFloat(b.unit_price) || 0) * (b.quantity || 1), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td colSpan={2}></td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="border-t pt-4 mt-4">
                <Button onClick={handleSubmitPricing} disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700">
                  {submitting ? 'Submitting...' : 'Submit Pricing Response'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
